/** @module hostology-api v3 */
/**
 * api.ts
 * Thin wrapper around the Hostology API Worker.
 * Base URL is set via VITE_API_BASE_URL in .env (e.g. https://api.hostology.io)
 */

import type {
  ValidateCredentialsResponse,
  StartIngestionResponse,
  IngestionJob,
} from "./types";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
if (!API_BASE) console.warn("[hostology] VITE_API_BASE_URL is not set — API calls will fail. Build v2.");
if (!API_BASE) console.warn("[hostology] VITE_API_BASE_URL is not set — API calls will fail. Build v2.");

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error((json as any)?.error ?? `Request failed (${res.status})`);
  }

  return res.json() as Promise<T>;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error((json as any)?.error ?? `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

// ── Endpoints ────────────────────────────────────────────────────────────────

/**
 * Validate a Mighty Networks API key + network ID pair.
 * Returns the network summary on success, throws on failure.
 */
export async function validateCredentials(
  apiKey: string,
  mnNetworkId: string
): Promise<ValidateCredentialsResponse> {
  return post("/api/validate-credentials", { api_key: apiKey, mn_network_id: mnNetworkId });
}

/**
 * Kick off a Phase 1 + Phase 2 ingestion job for a customer.
 * Returns the job ID immediately — job runs async in Cloudflare Queues.
 */
export async function startIngestion(
  customerId: string,
  mnNetworkId: string
): Promise<StartIngestionResponse> {
  return post("/api/ingestion/start", { customer_id: customerId, mn_network_id: mnNetworkId });
}

/**
 * Poll ingestion job status.
 * Call every ~3s during Phase 1 to drive the progress UI.
 */
export async function getJobStatus(jobId: string): Promise<IngestionJob> {
  return get(`/api/ingestion/${jobId}`);
}
